// Rules IDE editor island (item 11).
// Exposes window.SheeterEditor.mount(), called by the inline script in
// _EditorFile.cshtml every time a file is opened from the tree.
import * as monaco from "monaco-editor";
import { configureMonacoYaml } from "monaco-yaml";

// ---- workers ----------------------------------------------------------------
window.MonacoEnvironment = {
    getWorker(_moduleId, label) {
        return label === "yaml"
            ? new Worker("/js/dist/yaml.worker.js")
            : new Worker("/js/dist/editor.worker.js");
    },
};

// ---- schema wiring ------------------------------------------------------------
// Every pack file starts with our modeline convention:
//   # yaml-language-server: $schema=https://3p5sheeter/schemas/<x>.schema.json
// monaco-yaml honors that directive and it takes precedence over fileMatch -
// and https://3p5sheeter/ is a deliberately fake host (ERR_NAME_NOT_RESOLVED if
// fetched). So: fetch the real schema CONTENT from our /api/schemas/ endpoint
// up front, and register it under the fake canonical URIs the modelines (and
// their relative common.schema.json $refs) resolve to. Nothing is fetched from
// the fake host; fileMatch still covers files missing the modeline.
const CANON = "https://3p5sheeter/schemas/";

const folderToSchema = {
    "races": "race.schema.json",
    "classes": "class.schema.json",
    "skills": "skill.schema.json",
    "feats": "feat.schema.json",
    "weapons": "weapon.schema.json",
    "armor": "armor.schema.json",
    "equipment": "equipment.schema.json",
    "spells": "spell.schema.json",
    "languages": "language.schema.json",
};

async function fetchSchema(file) {
    // no-store: schemas evolve during development; a stale cached schema
    // makes the editor squiggle valid data (or bless invalid data).
    const resp = await fetch("/api/schemas/" + file, { cache: "no-store" });
    if (!resp.ok) { throw new Error("Failed to fetch schema " + file); }
    return resp.json();
}

const yamlReady = (async () => {
    const files = [...Object.values(folderToSchema), "pack.schema.json", "common.schema.json"];
    const contents = Object.fromEntries(await Promise.all(
        files.map(async (f) => [f, await fetchSchema(f)])));

    const schemas = Object.entries(folderToSchema).map(([folder, file]) => ({
        uri: CANON + file,
        fileMatch: ["**/" + folder + "/*.yaml"],
        schema: contents[file],
    }));
    schemas.push({
        uri: CANON + "pack.schema.json",
        fileMatch: ["**/pack.yaml"],
        schema: contents["pack.schema.json"],
    });
    // No fileMatch: present only as a $ref target for the entity schemas.
    schemas.push({
        uri: CANON + "common.schema.json",
        schema: contents["common.schema.json"],
    });

    configureMonacoYaml(monaco, { enableSchemaRequest: false, schemas });
})().catch((err) => console.error("monaco-yaml schema setup failed:", err));

// ---- reference completion (v1: all entity ids, labeled by type) ---------------
// The graph's entity ids are offered when the cursor is in a value position
// (after "key: " or a "- " list marker). Schema-position-exact x-refType
// filtering is a later refinement; schema validation catches wrong-type picks.
let entityIndex = null;

async function entityIds() {
    if (entityIndex === null) {
        const resp = await fetch("/Rules?handler=EntityIds");
        entityIndex = resp.ok ? await resp.json() : [];
    }
    return entityIndex;
}

monaco.languages.registerCompletionItemProvider("yaml", {
    async provideCompletionItems(model, position) {
        const lineToCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
        });
        // Value positions only: "someKey: <here>" or "- <here>".
        if (!/(?::|-)\s+[\w-]*$/.test(lineToCursor)) {
            return { suggestions: [] };
        }

        const ids = await entityIds();
        const word = model.getWordUntilPosition(position);
        const range = new monaco.Range(
            position.lineNumber, word.startColumn,
            position.lineNumber, word.endColumn);

        return {
            suggestions: ids.map((e) => ({
                label: e.id,
                kind: monaco.languages.CompletionItemKind.Reference,
                detail: e.type + " (" + e.pack + ")",
                insertText: e.id,
                range,
            })),
        };
    },
});

// ---- mount / save --------------------------------------------------------------
let editor = null;

function antiforgeryToken() {
    try {
        return JSON.parse(document.body.getAttribute("hx-headers"))
            .RequestVerificationToken;
    } catch {
        return "";
    }
}

window.SheeterEditor = {
    async mount() {
        await yamlReady; // schemas registered before the first model exists
        const container = document.getElementById("monaco-container");
        const dataEl = document.getElementById("editor-data");
        if (!container || !dataEl) { return; }
        const { path, content } = JSON.parse(dataEl.textContent);

        if (editor) { editor.dispose(); editor = null; }
        monaco.editor.getModels().forEach((m) => m.dispose());
        entityIndex = null; // refresh ids on each open; edits may have added some

        const model = monaco.editor.createModel(
            content, "yaml", monaco.Uri.parse("file:///" + path));
        editor = monaco.editor.create(container, {
            model,
            automaticLayout: true,
            minimap: { enabled: false },
            tabSize: 2,
            fixedOverflowWidgets: true,
            // Suggest as you type (not only on Ctrl+Space). strings:true matters
            // because YAML values tokenize as strings.
            quickSuggestions: { other: true, comments: false, strings: true },
            suggest: { showWords: false },
        });

        const dirtyEl = document.getElementById("editor-dirty");
        const saveBtn = document.getElementById("editor-save");
        let savedVersion = model.getAlternativeVersionId();

        function updateDirty() {
            const dirty = model.getAlternativeVersionId() !== savedVersion;
            if (dirtyEl) { dirtyEl.classList.toggle("d-none", !dirty); }
        }

        // ---- server validation (item 12): debounced splice-validate ----------
        const severities = {
            Error: monaco.MarkerSeverity.Error,
            Warning: monaco.MarkerSeverity.Warning,
            Info: monaco.MarkerSeverity.Info,
        };

        async function validate() {
            const body = new FormData();
            body.append("path", path);
            body.append("content", model.getValue());
            const resp = await fetch("/Rules?handler=Validate", {
                method: "POST",
                headers: { RequestVerificationToken: antiforgeryToken() },
                body,
            });
            if (!resp.ok) { return; } // panel keeps its last state
            const pane = document.getElementById("validation-pane");
            if (pane) {
                pane.innerHTML = await resp.text();
                // The panel carries hx-* attributes (packsChanged listener);
                // content set via innerHTML needs explicit htmx activation.
                if (window.htmx) { window.htmx.process(pane); }
            }

            const dataEl = document.getElementById("validation-markers");
            if (!dataEl || !editor || editor.getModel() !== model) { return; }
            const markers = JSON.parse(dataEl.textContent).map((m) => ({
                severity: severities[m.severity] ?? monaco.MarkerSeverity.Info,
                message: m.message,
                startLineNumber: m.line,
                startColumn: m.column,
                endLineNumber: m.line,
                endColumn: 1000, // underline from the located value to EOL (Monaco clamps)
            }));
            monaco.editor.setModelMarkers(model, "server", markers);
        }

        let validateTimer = null;
        function scheduleValidate() {
            if (validateTimer) { clearTimeout(validateTimer); }
            validateTimer = setTimeout(validate, 750);
        }

        model.onDidChangeContent(() => {
            updateDirty();
            scheduleValidate();
        });
        validate(); // immediate results for the just-opened file

        async function save() {
            const body = new FormData();
            body.append("path", path);
            body.append("content", model.getValue());
            const resp = await fetch("/Rules?handler=Save", {
                method: "POST",
                headers: { RequestVerificationToken: antiforgeryToken() },
                body,
            });
            if (resp.ok) {
                savedVersion = model.getAlternativeVersionId();
                updateDirty();
            } else {
                window.alert(await resp.text() || "Save failed.");
            }
        }

        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, save);
        if (saveBtn) { saveBtn.addEventListener("click", save); }

        // Problems-panel click-through on a not-yet-open file (rules-ide.js
        // stashes the target line before loading the file into the pane).
        if (window.__pendingRevealLine) {
            const pending = window.__pendingRevealLine;
            window.__pendingRevealLine = null;
            window.SheeterEditor.revealLine(pending);
        }
    },

    /** Item 12 uses this to push server-side diagnostics into the editor. */
    setMarkers(markers) {
        const model = editor && editor.getModel();
        if (model) { monaco.editor.setModelMarkers(model, "server", markers); }
    },

    /** Jump to a line (Problems panel click-through). */
    revealLine(line) {
        if (!editor) { return; }
        editor.revealLineInCenter(line);
        editor.setPosition({ lineNumber: line, column: 1 });
        editor.focus();
    },
};
